import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Wallet,
  Clock,
  Save,
  Edit2,
  Package,
  CheckCircle,
  XCircle,
  Plus,
  X,
  Trash2,
  RotateCcw,
  Loader2,
} from "lucide-react";
import {
  getPaymentConfig,
  updatePaymentConfig,
  getPaymentPackages,
  createPaymentPackage,
  updatePaymentPackage,
  deletePaymentPackage,
  restorePaymentPackage,
  getWebhookSyncStatus,
} from "../../api/ginoxAiApi";
import ConfirmationModal from "../../components/ConfirmationModal";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const AIPayments = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, packageId: null, packageName: "" });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [packages, setPackages] = useState([]);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [recipientAddress, setRecipientAddress] = useState("");
  const [activeTab, setActiveTab] = useState("config");
  const [newPackage, setNewPackage] = useState({ name: "", credits: "", price: "", currency: "USDT" });
  const [editPackageData, setEditPackageData] = useState({ name: "", credits: "", price: "", currency: "", isActive: true });

  const fetchPaymentConfig = async () => {
    try {
      const res = await getPaymentConfig();
      setPaymentConfig(res.data);
      setRecipientAddress(res.data?.recipientAddress || "");
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error(err.response?.data?.message || "Failed to fetch payment config");
      }
    }
  };

  const fetchWebhookStatus = async () => {
    try {
      const res = await getWebhookSyncStatus();
      setWebhookStatus(res.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Failed to fetch webhook status");
      }
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await getPaymentPackages();
      setPackages(res.data?.packages || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch packages");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchPaymentConfig(), fetchPackages(), fetchWebhookStatus()]);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!recipientAddress.trim()) {
      toast.error("Recipient address is required");
      return;
    }
    setSaving(true);
    try {
      const res = await updatePaymentConfig(recipientAddress);
      setPaymentConfig(res.data);
      setIsEditing(false);
      const webhookSync = res.data?.webhookSync || [];
      const failedNetworks = webhookSync.filter((w) => !w.success);
      if (failedNetworks.length > 0) {
        const failedNames = failedNetworks.map((w) => w.network).join(", ");
        toast.warning(`Address updated but webhook sync failed for: ${failedNames}`, { autoClose: 8000 });
      } else if (webhookSync.length > 0) {
        toast.success(`Payment settings updated and synced to ${webhookSync.length} networks`);
      } else {
        toast.success("Payment settings updated successfully");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update payment config");
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePackage = async () => {
    if (!newPackage.name.trim() || !newPackage.credits || !newPackage.price) {
      toast.error("Please fill all required fields");
      return;
    }
    setCreating(true);
    try {
      await createPaymentPackage({
        name: newPackage.name,
        credits: Number(newPackage.credits),
        price: Number(newPackage.price),
        currency: newPackage.currency,
      });
      toast.success("Package created successfully");
      setShowCreateForm(false);
      setNewPackage({ name: "", credits: "", price: "", currency: "USDT" });
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create package");
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (pkg) => {
    setEditingPackage(pkg.id);
    setEditPackageData({ name: pkg.name, credits: pkg.credits, price: pkg.price, currency: pkg.currency, isActive: pkg.isActive });
  };

  const handleUpdatePackage = async () => {
    if (!editPackageData.name.trim() || !editPackageData.credits || !editPackageData.price) {
      toast.error("Please fill all required fields");
      return;
    }
    setUpdating(true);
    try {
      await updatePaymentPackage(editingPackage, {
        name: editPackageData.name,
        credits: Number(editPackageData.credits),
        price: Number(editPackageData.price),
        currency: editPackageData.currency,
        isActive: editPackageData.isActive,
      });
      toast.success("Package updated successfully");
      setEditingPackage(null);
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update package");
    } finally {
      setUpdating(false);
    }
  };

  const openDeleteModal = (pkg) => {
    setDeleteModal({ isOpen: true, packageId: pkg.id, packageName: pkg.name });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, packageId: null, packageName: "" });
  };

  const handleDeletePackage = async () => {
    const id = deleteModal.packageId;
    setDeleting(id);
    try {
      await deletePaymentPackage(id);
      toast.success("Package deleted successfully");
      closeDeleteModal();
      fetchPackages();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete package");
    } finally {
      setDeleting(null);
    }
  };

  const handleRestorePackage = async (id) => {
    setRestoring(id);
    try {
      await restorePaymentPackage(id);
      toast.success("Package restored successfully");
      fetchPackages();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("Package is already active");
      } else {
        toast.error(err.response?.data?.message || "Failed to restore package");
      }
    } finally {
      setRestoring(null);
    }
  };

  const handleCancel = () => {
    setRecipientAddress(paymentConfig?.recipientAddress || "");
    setIsEditing(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">AI <span className="text-gradient-ginox">Payments</span></h1>
      <p className="text-sm text-muted-foreground mb-6">
        Manage payment configuration and credit packages.
      </p>

      {/* Tabs */}
      <div className="flex overflow-x-auto mb-6 border-b border-border/70 gap-1">
        <button
          onClick={() => setActiveTab("config")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition whitespace-nowrap -mb-px ${
            activeTab === "config"
              ? "border-ginox-green text-ginox-green"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Wallet className="w-3.5 h-3.5" /> Payment Config
        </button>
        <button
          onClick={() => setActiveTab("packages")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition whitespace-nowrap -mb-px ${
            activeTab === "packages"
              ? "border-ginox-green text-ginox-green"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Package className="w-3.5 h-3.5" /> Packages
        </button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-12">Loading...</div>
      ) : (
        <>
          {/* Config Tab */}
          {activeTab === "config" && (
            <div className="border-t border-white/[0.08] pt-6">
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm font-semibold text-foreground">Payment Configuration</p>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-border/70 bg-white/[0.02] p-4">
                  <div className="flex items-start gap-3">
                    <Wallet className="text-ginox-green w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-[0.06em] font-medium">
                        Recipient Address
                      </p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={recipientAddress}
                          onChange={(e) => setRecipientAddress(e.target.value)}
                          placeholder="0x742d35Cc6634C0532925a3b8448c9e7595f0bEb0"
                          className={inputCls + " font-mono"}
                        />
                      ) : (
                        <p className="text-foreground font-mono text-sm break-all">
                          {paymentConfig?.recipientAddress || "Not configured"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {paymentConfig?.updatedAt && (
                  <div className="rounded-lg border border-border/70 bg-white/[0.02] p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="text-ginox-green w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground mb-1 uppercase tracking-[0.06em] font-medium">
                          Last Updated
                        </p>
                        <p className="text-sm text-foreground font-mono">{formatDate(paymentConfig.updatedAt)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {isEditing && (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Webhook Sync Status */}
                {webhookStatus?.webhooks && webhookStatus.webhooks.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/40">
                    <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                      Webhook Sync Status
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {webhookStatus.webhooks.map((webhook) => (
                        <div
                          key={webhook.network}
                          className={`flex items-center justify-between p-2 rounded-md text-sm border ${
                            webhook.synced
                              ? "bg-emerald-500/10 border-emerald-500/20"
                              : "bg-red-500/10 border-red-500/20"
                          }`}
                        >
                          <span className="text-xs text-foreground truncate">{webhook.network}</span>
                          {webhook.synced ? (
                            <CheckCircle className="text-emerald-400 w-3.5 h-3.5 ml-2 flex-shrink-0" />
                          ) : (
                            <XCircle className="text-red-400 w-3.5 h-3.5 ml-2 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                    {webhookStatus.webhooks.some((w) => !w.synced) && (
                      <p className="mt-2 text-xs text-red-400">
                        Some webhooks are out of sync. Update the address to resync.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === "packages" && (
            <div className="space-y-6">
              <div className="border-t border-white/[0.08] pt-6">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-sm font-semibold text-foreground">Credit Packages</p>
                  {!showCreateForm && (
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Package
                    </button>
                  )}
                </div>

                {showCreateForm && (
                  <div className="mb-6 p-4 rounded-lg border border-border/70 bg-white/[0.02]">
                    <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-4">
                      Create New Package
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={newPackage.name}
                          onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                          placeholder="Starter Package"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Credits *
                        </label>
                        <input
                          type="number"
                          value={newPackage.credits}
                          onChange={(e) => setNewPackage({ ...newPackage, credits: e.target.value })}
                          placeholder="1000"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Price *
                        </label>
                        <input
                          type="number"
                          value={newPackage.price}
                          onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                          placeholder="10"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Currency
                        </label>
                        <input
                          type="text"
                          value={newPackage.currency}
                          onChange={(e) => setNewPackage({ ...newPackage, currency: e.target.value })}
                          placeholder="USDT"
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleCreatePackage}
                        disabled={creating}
                        className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        Create
                      </button>
                      <button
                        onClick={() => { setShowCreateForm(false); setNewPackage({ name: "", credits: "", price: "", currency: "USDT" }); }}
                        disabled={creating}
                        className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {packages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No packages available</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`rounded-lg border p-4 ${
                          pkg.isActive
                            ? "bg-emerald-500/[0.05] border-emerald-500/20"
                            : "bg-white/[0.02] border-border/70"
                        }`}
                      >
                        {editingPackage === pkg.id ? (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs text-muted-foreground mb-1">Name</label>
                              <input
                                type="text"
                                value={editPackageData.name}
                                onChange={(e) => setEditPackageData({ ...editPackageData, name: e.target.value })}
                                className={inputCls}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">Credits</label>
                                <input
                                  type="number"
                                  value={editPackageData.credits}
                                  onChange={(e) => setEditPackageData({ ...editPackageData, credits: e.target.value })}
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">Price</label>
                                <input
                                  type="number"
                                  value={editPackageData.price}
                                  onChange={(e) => setEditPackageData({ ...editPackageData, price: e.target.value })}
                                  className={inputCls}
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-muted-foreground mb-1">Currency</label>
                              <input
                                type="text"
                                value={editPackageData.currency}
                                onChange={(e) => setEditPackageData({ ...editPackageData, currency: e.target.value })}
                                className={inputCls}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`active-${pkg.id}`}
                                checked={editPackageData.isActive}
                                onChange={(e) => setEditPackageData({ ...editPackageData, isActive: e.target.checked })}
                                className="rounded"
                              />
                              <label htmlFor={`active-${pkg.id}`} className="text-xs text-muted-foreground">Active</label>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={handleUpdatePackage}
                                disabled={updating}
                                className="flex-1 flex items-center justify-center py-1.5 rounded-md border border-ginox-green/60 bg-ginox-green/10 text-xs text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
                              >
                                {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => setEditingPackage(null)}
                                disabled={updating}
                                className="flex-1 flex items-center justify-center py-1.5 rounded-md border border-border/70 bg-white/[0.02] text-xs text-muted-foreground hover:bg-white/[0.05] transition-colors disabled:opacity-50"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold text-foreground">{pkg.name}</h3>
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleEditClick(pkg)} className="text-muted-foreground hover:text-foreground transition-colors">
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                {pkg.isActive ? (
                                  <button
                                    onClick={() => openDeleteModal(pkg)}
                                    disabled={deleting === pkg.id}
                                    className="text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                                  >
                                    {deleting === pkg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleRestorePackage(pkg.id)}
                                    disabled={restoring === pkg.id}
                                    className="text-muted-foreground hover:text-emerald-400 transition-colors disabled:opacity-50"
                                  >
                                    {restoring === pkg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                  </button>
                                )}
                                {pkg.isActive ? (
                                  <CheckCircle className="text-emerald-400 w-3.5 h-3.5" />
                                ) : (
                                  <XCircle className="text-muted-foreground w-3.5 h-3.5" />
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-xs text-muted-foreground">Credits</span>
                                <span className="text-xs font-mono text-foreground">{pkg.credits?.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-xs text-muted-foreground">Price</span>
                                <span className="text-xs font-mono text-foreground">{pkg.price} {pkg.currency}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-xs text-muted-foreground">Status</span>
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${pkg.isActive ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20" : "bg-white/[0.05] text-muted-foreground border border-border/50"}`}>
                                  {pkg.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-border/40 text-xs text-muted-foreground font-mono">
                              Updated: {formatDate(pkg.updatedAt)}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeletePackage}
        title="Delete Package"
        message={`Are you sure you want to delete "${deleteModal.packageName}"? This will set it as inactive.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting !== null}
        loadingText="Deleting..."
        danger={true}
      />
    </div>
  );
};

export default AIPayments;
